import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Collapse,
} from '@material-ui/core';
import { Add, ExpandLess, ExpandMore } from '@material-ui/icons';
import { ImportQueue } from './ImportQueue';
import { getGmailStatus } from '../core/services/import.service';
import { getCategories, getTags } from '../core/services/tasks.service';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { useStyles } from './cashflow.styles';
import {
    VHEAD,
    VCELL,
    VROW_BASE,
    VTOTAL,
    VWRAP,
    VHEAD_ROW,
    CHIP_DEBIT,
    CHIP_CREDIT,
    ROW_BTN,
    SELECT_CIRCLE,
    SELECT_CIRCLE_ACTIVE,
} from './cashflow.styles';
import {
    ROW_HEIGHT,
    ENTRIES_COLS,
    ENTRIES_COLS_MOBILE,
    ENTRIES_COLS_MOBILE_BROWSE,
} from './cashflow.constants';
import { fmt, fmtDate, computeEntriesTotals } from './cashflow.utils';
import { EntryFormDialog, EntryFormValues } from './EntryFormDialog';
import { BulkAddDialog, BulkRow } from './BulkAddDialog';
import { DuplicateDialog } from './DuplicateDialog';
import { SaveTemplateDialog } from './SaveTemplateDialog';
import { duplicateEntries } from '../core/services/templates.service';
import { Edit, Delete } from '@material-ui/icons';

// ── EntriesRow virtual-list row ──

interface EntriesRowData {
    items: any[];
    selectedIds: Set<string>;
    onToggle: (id: string) => void;
    onEdit: (id: string) => void;
    onDelete: (entry: any) => void;
    atRiskDates: Set<string>;
    isMobile: boolean;
    selectMode: boolean;
    onLongPress: (id: string) => void;
}

// ── Memoized virtual-list row component ──
// Defined at module level so react-window can bail out on unchanged rows.
const EntriesRow = React.memo(({ index, style, data }: ListChildComponentProps) => {
    const { items, selectedIds, onToggle, onEdit, onDelete, atRiskDates, isMobile, selectMode, onLongPress } = data as EntriesRowData;
    const e = items[index];
    const isSelected = selectedIds.has(e.id);
    const isAtRisk = e.date && atRiskDates.has(e.date);

    // Mobile: swipe-to-reveal action buttons + long-press for select mode
    const touchStartX = React.useRef(0);
    const touchStartY = React.useRef(0);
    const [offset, setOffset] = React.useState(0);
    const [isSnapping, setIsSnapping] = React.useState(false);
    const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const didMove = React.useRef(false);
    const didLongPress = React.useRef(false);

    // Clean up long-press timer on unmount (row can scroll out of viewport)
    React.useEffect(() => {
        return () => {
            if (longPressTimer.current) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
            }
        };
    }, []);

    // Reset swipe offset when entering select mode to prevent stale jump on exit
    React.useEffect(() => {
        if (selectMode) {
            setOffset(0);
            setIsSnapping(false);
        }
    }, [selectMode]);

    const isOnCreditMobile = !!e.onCredit;
    if (isMobile) {
        const rowBg = isSelected ? '#eff6ff' : isOnCreditMobile ? '#faf5ff' : isAtRisk ? '#fff1f2' : 'white';
        const cols = selectMode ? ENTRIES_COLS_MOBILE : ENTRIES_COLS_MOBILE_BROWSE;
        return (
            <div style={{ ...style, position: 'relative', overflow: 'hidden' }}>
                {/* Revealed action buttons behind the row (only in browse mode) */}
                {!selectMode && (
                    <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, display: 'flex', width: 112 }}>
                        <button
                            style={{ flex: 1, background: '#2563eb', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => { setOffset(0); onEdit(e.id); }}
                        >
                            <Edit style={{ fontSize: 18 }} />
                        </button>
                        <button
                            style={{ flex: 1, background: '#dc2626', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => { setOffset(0); onDelete(e); }}
                        >
                            <Delete style={{ fontSize: 18 }} />
                        </button>
                    </div>
                )}
                {/* Sliding row content */}
                <div
                    style={{
                        ...VROW_BASE,
                        height: '100%',
                        gridTemplateColumns: cols,
                        backgroundColor: rowBg,
                        ...(isOnCreditMobile ? { borderLeft: '3px solid #7c3aed' } : isAtRisk ? { borderLeft: '3px solid #fca5a5' } : {}),
                        transform: selectMode ? 'none' : `translateX(${offset}px)`,
                        transition: isSnapping ? 'transform 0.2s ease' : 'none',
                        position: 'relative',
                        zIndex: 1,
                    }}
                    onClick={() => { if (selectMode && !didLongPress.current && !didMove.current) onToggle(e.id); }}
                    onTouchStart={(ev) => {
                        touchStartX.current = ev.touches[0].clientX;
                        touchStartY.current = ev.touches[0].clientY;
                        didMove.current = false;
                        didLongPress.current = false;
                        setIsSnapping(false);
                        if (!selectMode) {
                            longPressTimer.current = setTimeout(() => {
                                if (!didMove.current) {
                                    didLongPress.current = true;
                                    onLongPress(e.id);
                                }
                            }, 400);
                        }
                    }}
                    onTouchMove={(ev) => {
                        const dx = ev.touches[0].clientX - touchStartX.current;
                        const dy = ev.touches[0].clientY - touchStartY.current;
                        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
                            didMove.current = true;
                            if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
                        }
                        if (!selectMode && dx < 0) setOffset(Math.max(-112, dx));
                    }}
                    onTouchEnd={() => {
                        if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
                        if (!selectMode) {
                            setIsSnapping(true);
                            setOffset(offset < -56 ? -112 : 0);
                        }
                    }}
                >
                    {selectMode && (
                        <div style={{ ...VCELL, padding: '2px 6px', justifyContent: 'center' }}>
                            <div style={isSelected ? SELECT_CIRCLE_ACTIVE : SELECT_CIRCLE}>
                                {isSelected && (
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    )}
                    <div style={VCELL}>{e.title}</div>
                    <div style={{ ...VCELL, justifyContent: 'flex-end', color: e.type === 'CREDIT' ? '#16a34a' : '#dc2626' }}>
                        {e.type === 'CREDIT' ? '+' : ''}{fmt(Number(e.amount))}
                    </div>
                    <div style={{ ...VCELL, color: '#64748b', fontSize: '0.72rem' }}>{e.date ? fmtDate(e.date) : '—'}</div>
                </div>
            </div>
        );
    }

    const isOnCredit = !!e.onCredit;
    const desktopBg = isSelected ? '#eff6ff' : isOnCredit ? '#faf5ff' : isAtRisk ? '#fff1f2' : 'white';
    const desktopBorder = isOnCredit ? { borderLeft: '3px solid #7c3aed' } : isAtRisk ? { borderLeft: '3px solid #fca5a5' } : {};

    return (
        <div style={{ ...VROW_BASE, ...style, gridTemplateColumns: ENTRIES_COLS, backgroundColor: desktopBg, ...desktopBorder }}>
            <div style={{ ...VCELL, padding: '2px 6px' }}>
                <Checkbox size="small" disableRipple style={{ padding: 2, color: '#94a3b8' }}
                    checked={isSelected} onChange={() => onToggle(e.id)} />
            </div>
            <div style={VCELL}>{e.title}</div>
            <div style={VCELL}>
                <span style={e.type === 'CREDIT' ? CHIP_CREDIT : CHIP_DEBIT}>
                    {e.type === 'CREDIT' ? 'Income' : 'Bill'}
                </span>
            </div>
            <div style={{ ...VCELL, color: '#64748b', fontSize: '0.72rem' }}>{e.category || '—'}</div>
            <div style={{ ...VCELL, color: '#64748b', fontSize: '0.72rem' }}>{e.tag || '—'}</div>
            <div style={{ ...VCELL, justifyContent: 'flex-end', color: e.type === 'CREDIT' ? '#16a34a' : '#dc2626' }}>
                {e.type === 'CREDIT' ? '+' : ''}{fmt(Number(e.amount))}
            </div>
            <div style={{ ...VCELL, color: '#64748b' }}>{e.date ? fmtDate(e.date) : '—'}</div>
            <div style={{ ...VCELL, padding: '2px 4px', gap: 0 }}>
                <button style={ROW_BTN} title="Edit" onClick={() => onEdit(e.id)}>
                    <Edit style={{ fontSize: 14 }} />
                </button>
                <button style={{ ...ROW_BTN, marginLeft: 2 }} title="Delete" onClick={() => onDelete(e)}>
                    <Delete style={{ fontSize: 14 }} />
                </button>
            </div>
        </div>
    );
});

// ── AutoHeight helper ──
const AutoHeight: React.FC<{ children: (height: number) => React.ReactNode }> = ({ children }) => {
    const [height, setHeight] = React.useState(400);
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const ro = new ResizeObserver(([entry]) => setHeight(entry.contentRect.height));
        ro.observe(node);
        return () => ro.disconnect();
    }, []);
    return <div ref={ref} style={{ flex: 1, minHeight: 0, height: '100%' }}>{children(height)}</div>;
};

// ── EntriesTab props ──

interface EntriesTabProps {
    entries: any[];
    addEntrySignal: any;
    updateEntriesSignal: any;
    atRiskDates: Set<string>;
    isMobile: boolean;
    onEntryEdit: (id: string) => void;
    // Filter state passed from parent so projection tab row click can pre-set them
    entryDateFrom: string;
    entryDateTo: string;
    setEntryDateFrom: (v: string) => void;
    setEntryDateTo: (v: string) => void;
    // Increments each time the Header "+" button is pressed — opens the add bill form
    openAddFormSeq?: number;
    // Gmail import queue
    userId?: string;
    listId?: string;
    onEntriesChanged?: () => void;
}

export const EntriesTab: React.FC<EntriesTabProps> = ({
    entries,
    addEntrySignal,
    updateEntriesSignal,
    atRiskDates,
    isMobile,
    onEntryEdit,
    entryDateFrom,
    entryDateTo,
    setEntryDateFrom,
    setEntryDateTo,
    openAddFormSeq,
    userId,
    listId,
    onEntriesChanged,
}) => {
    const classes = useStyles();

    // Persist Entries filter/sort across edit navigation using sessionStorage
    const [entrySearch, setEntrySearch] = useState(() => sessionStorage.getItem('ef_search') || '');
    const [entryTypeFilter, setEntryTypeFilter] = useState<'all' | 'DEBIT' | 'CREDIT'>(() => (sessionStorage.getItem('ef_type') as any) || 'all');
    const [entryTagFilter, setEntryTagFilter] = useState(() => sessionStorage.getItem('ef_tag') || 'all');
    const [entryCategoryFilter, setEntryCategoryFilter] = useState(() => sessionStorage.getItem('ef_cat') || 'all');
    const [onCreditFilter, setOnCreditFilter] = useState(() => sessionStorage.getItem('ef_oncredit') === 'true');
    const [entrySortCol, setEntrySortCol] = useState<string | null>(() => sessionStorage.getItem('ef_sortcol') || null);
    const [entrySortDir, setEntrySortDir] = useState<'asc' | 'desc'>(() => (sessionStorage.getItem('ef_sortdir') as any) || 'asc');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [selectMode, setSelectMode] = useState(false);
    const [bulkTagInput, setBulkTagInput] = useState('');
    const [showBulkTagDialog, setShowBulkTagDialog] = useState(false);
    const [bulkCategoryInput, setBulkCategoryInput] = useState('');
    const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false);
    const [bulkDateInput, setBulkDateInput] = useState('');
    const [showBulkDateDialog, setShowBulkDateDialog] = useState(false);
    const [bulkAmountInput, setBulkAmountInput] = useState('');
    const [showBulkAmountDialog, setShowBulkAmountDialog] = useState(false);

    const [addFormType, setAddFormType] = useState<'DEBIT' | 'CREDIT' | null>(null);
    const [showBulkAdd, setShowBulkAdd] = useState(false);
    const [showDuplicate, setShowDuplicate] = useState(false);
    const [showSaveTemplate, setShowSaveTemplate] = useState(false);

    // ── Gmail import queue ──
    const [importQueueOpen, setImportQueueOpen] = useState(false);
    const [importDraftCount, setImportDraftCount] = useState(0);

    useEffect(() => {
        if (!userId) return;
        getGmailStatus(userId)
            .then(s => { if (s.connected) setImportDraftCount(s.draftCount); })
            .catch(() => { /* non-critical — silently ignore */ });
    }, [userId]);

    // Open the add-bill form when the parent signals it (Header "+" button)
    const lastSeenSeq = React.useRef(openAddFormSeq ?? 0);
    useEffect(() => {
        if (openAddFormSeq && openAddFormSeq !== lastSeenSeq.current) {
            lastSeenSeq.current = openAddFormSeq;
            setAddFormType('DEBIT');
        }
    }, [openAddFormSeq]);

    // Persisted setters
    const setEntrySearchP = (v: string) => { setEntrySearch(v); sessionStorage.setItem('ef_search', v); };
    const setEntryDateFromP = useCallback((v: string) => { setEntryDateFrom(v); sessionStorage.setItem('ef_from', v); }, [setEntryDateFrom]);
    const setEntryDateToP = useCallback((v: string) => { setEntryDateTo(v); sessionStorage.setItem('ef_to', v); }, [setEntryDateTo]);
    const setEntryTypeFilterP = (v: 'all' | 'DEBIT' | 'CREDIT') => { setEntryTypeFilter(v); sessionStorage.setItem('ef_type', v); };
    const setEntryTagFilterP = (v: string) => { setEntryTagFilter(v); sessionStorage.setItem('ef_tag', v); };
    const setEntryCategoryFilterP = (v: string) => { setEntryCategoryFilter(v); sessionStorage.setItem('ef_cat', v); };
    const setOnCreditFilterP = useCallback((v: boolean) => { setOnCreditFilter(v); sessionStorage.setItem('ef_oncredit', String(v)); }, []);

    // Computed
    const clientCategories = useMemo(() => Array.from(new Set(entries.map((e: any) => e.category).filter(Boolean))).sort() as string[], [entries]);
    const [apiCategories, setApiCategories] = useState<string[]>([]);
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        getCategories(userId)
            .then((res: any) => {
                if (!cancelled && Array.isArray(res?.categories)) {
                    setApiCategories(res.categories.map((c: any) => String(c)));
                }
            })
            .catch(() => { /* non-critical */ });
        return () => { cancelled = true; };
    }, [userId]);
    const allCategories = useMemo(
        () => Array.from(new Set([...clientCategories, ...apiCategories])).sort() as string[],
        [clientCategories, apiCategories]
    );
    const [apiTags, setApiTags] = useState<string[]>([]);
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        getTags(userId)
            .then((res: any) => {
                if (!cancelled && Array.isArray(res?.tags)) {
                    setApiTags(res.tags.map((t: any) => String(t)));
                }
            })
            .catch(() => { /* non-critical */ });
        return () => { cancelled = true; };
    }, [userId]);
    const clientTags = useMemo(() => Array.from(new Set(
        entries.flatMap((e: any) => e.tag ? e.tag.split(',').map((s: string) => s.trim()).filter(Boolean) : [])
    )).sort() as string[], [entries]);
    const allTags = useMemo(
        () => Array.from(new Set([...clientTags, ...apiTags])).sort() as string[],
        [clientTags, apiTags]
    );

    const filteredEntries = useMemo(() => {
        const filtered = entries.filter((e: any) => {
            if (entrySearch) {
                const q = entrySearch.toLowerCase();
                const cat = (e.category || '').toLowerCase();
                const tag = (e.tag || '').toLowerCase();
                if (!e.title?.toLowerCase().includes(q) && !cat.includes(q) && !tag.includes(q)) return false;
            }
            if (entryTypeFilter !== 'all' && e.type !== entryTypeFilter) return false;
            if (entryCategoryFilter !== 'all' && (e.category || '').toLowerCase() !== entryCategoryFilter.toLowerCase()) return false;
            if (entryTagFilter !== 'all' && !(e.tag ? e.tag.split(',').map((s: string) => s.trim().toLowerCase()) : []).includes(entryTagFilter.toLowerCase())) return false;
            if (entryDateFrom && (e.date || '') < entryDateFrom) return false;
            if (entryDateTo && (e.date || '') > entryDateTo) return false;
            if (onCreditFilter && !e.onCredit) return false;
            return true;
        });
        if (!entrySortCol) return filtered;
        return [...filtered].sort((a, b) => {
            let av: any, bv: any;
            if (entrySortCol === 'amount') { av = Number(a.amount); bv = Number(b.amount); }
            else if (entrySortCol === 'date') { av = a.date ? new Date(a.date).getTime() : 0; bv = b.date ? new Date(b.date).getTime() : 0; }
            else { av = (a[entrySortCol] || '').toLowerCase(); bv = (b[entrySortCol] || '').toLowerCase(); }
            if (av < bv) return entrySortDir === 'asc' ? -1 : 1;
            if (av > bv) return entrySortDir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [entries, entrySearch, entryDateFrom, entryDateTo, entryTypeFilter, entryTagFilter, entryCategoryFilter, onCreditFilter, entrySortCol, entrySortDir]);

    const handleSortCol = (col: string) => {
        if (entrySortCol === col) {
            const next = entrySortDir === 'asc' ? 'desc' : 'asc';
            setEntrySortDir(next); sessionStorage.setItem('ef_sortdir', next);
        } else {
            setEntrySortCol(col); sessionStorage.setItem('ef_sortcol', col);
            setEntrySortDir('asc'); sessionStorage.setItem('ef_sortdir', 'asc');
        }
    };

    const nSelected = filteredEntries.filter((e: any) => selectedIds.has(e.id)).length;
    const allFilteredSelected = filteredEntries.length > 0 && filteredEntries.every((e: any) => selectedIds.has(e.id));

    const toggleSelect = useCallback((id: string) => setSelectedIds(prev => {
        const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
    }), []);
    const onLongPress = useCallback((id: string) => {
        setSelectMode(true);
        setSelectedIds(new Set([id]));
    }, []);
    const exitSelectMode = useCallback(() => {
        setSelectMode(false);
        setSelectedIds(new Set());
    }, []);
    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            setSelectedIds(prev => { const next = new Set(prev); filteredEntries.forEach((e: any) => next.delete(e.id)); return next; });
        } else {
            setSelectedIds(prev => { const next = new Set(prev); filteredEntries.forEach((e: any) => next.add(e.id)); return next; });
        }
    };

    const handleDeleteEntry = useCallback((entry: any) => {
        updateEntriesSignal.dispatch(entries.filter((e: any) => e.id !== entry.id));
    }, [entries, updateEntriesSignal]);

    const handleBulkDelete = () => {
        updateEntriesSignal.dispatch(entries.filter((e: any) => !selectedIds.has(e.id)));
        setSelectedIds(new Set());
    };
    const handleBulkSetType = (type: string) => {
        updateEntriesSignal.dispatch(entries.map((e: any) => selectedIds.has(e.id) ? { ...e, type } : e));
        setSelectedIds(new Set());
    };
    const handleBulkToggleOnCredit = () => {
        const selectedDebits = filteredEntries.filter((e: any) => selectedIds.has(e.id) && e.type === 'DEBIT');
        const allOnCredit = selectedDebits.length > 0 && selectedDebits.every((e: any) => e.onCredit);
        updateEntriesSignal.dispatch(entries.map((e: any) =>
            selectedIds.has(e.id) && e.type === 'DEBIT' ? { ...e, onCredit: !allOnCredit } : e
        ));
        setSelectedIds(new Set());
    };
    const handleBulkSetTag = () => {
        updateEntriesSignal.dispatch(entries.map((e: any) => selectedIds.has(e.id) ? { ...e, tag: bulkTagInput } : e));
        setSelectedIds(new Set()); setShowBulkTagDialog(false); setBulkTagInput('');
    };
    const handleBulkSetCategory = () => {
        updateEntriesSignal.dispatch(entries.map((e: any) => selectedIds.has(e.id) ? { ...e, category: bulkCategoryInput } : e));
        setSelectedIds(new Set()); setShowBulkCategoryDialog(false); setBulkCategoryInput('');
    };
    const handleBulkSetDate = () => {
        updateEntriesSignal.dispatch(entries.map((e: any) => selectedIds.has(e.id) ? { ...e, date: bulkDateInput || null } : e));
        setSelectedIds(new Set()); setShowBulkDateDialog(false); setBulkDateInput('');
    };
    const handleBulkSetAmount = () => {
        const amt = parseFloat(bulkAmountInput);
        if (isNaN(amt)) return;
        updateEntriesSignal.dispatch(entries.map((e: any) => selectedIds.has(e.id) ? { ...e, amount: amt } : e));
        setSelectedIds(new Set()); setShowBulkAmountDialog(false); setBulkAmountInput('');
    };

    // Totals
    const entriesTotals = useMemo(() => computeEntriesTotals(filteredEntries), [filteredEntries]);
    const selectedEntriesTotals = useMemo(() => {
        if (selectedIds.size === 0) return null;
        const selected = filteredEntries.filter((e: any) => selectedIds.has(e.id));
        return selected.length > 0 ? computeEntriesTotals(selected) : null;
    }, [filteredEntries, selectedIds]);
    const activeTotals = selectedEntriesTotals || entriesTotals;
    const totalFilteredCredits = activeTotals.credits;
    const totalFilteredDebits = activeTotals.debits;
    const totalFilteredNet = activeTotals.net;

    // Virtual list item data
    const entriesData = useMemo<EntriesRowData>(() => ({
        items: filteredEntries,
        selectedIds,
        onToggle: toggleSelect,
        onEdit: onEntryEdit,
        onDelete: handleDeleteEntry,
        atRiskDates,
        selectMode,
        onLongPress,
        isMobile,
    }), [filteredEntries, selectedIds, toggleSelect, onEntryEdit, handleDeleteEntry, atRiskDates, selectMode, onLongPress, isMobile]);

    const handleInlineSave = ({ title, amount, type, category, tag, date, onCredit }: EntryFormValues) => {
        addEntrySignal.dispatch({ title, amount: Number(amount), type, category, tag, date: date || null, onCredit: !!onCredit });
        setAddFormType(null);
    };

    const handleBulkAddSave = (rows: BulkRow[]) => {
        const genId = () =>
            'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                const r = (Math.random() * 16) | 0;
                return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
            });
        const newEntries = rows.map((row, i) => ({
            id: genId(),
            title: row.title,
            order: entries.length + i,
            payments: [],
            ignore: false,
            parent: undefined,
            type: row.type,
            tag: row.tag,
            category: row.category,
            amount: Number(row.amount),
            complete: false,
            priority: false,
            date: row.date || null,
            onCredit: row.onCredit || false,
            reminderDate: null,
            isReminderOn: false,
        }));
        updateEntriesSignal.dispatch([...entries, ...newEntries]);
        setShowBulkAdd(false);
    };

    const handleDuplicate = async (interval: 'weekly' | 'monthly', count: number, startFrom?: string) => {
        if (!userId || !listId) return;
        const ids = Array.from(selectedIds);
        await duplicateEntries(userId, listId, ids, interval, count, startFrom);
        setSelectedIds(new Set());
        setShowDuplicate(false);
        if (onEntriesChanged) onEntriesChanged();
    };

    return (
        <Box className={classes.tabPanel} style={{ padding: '12px 16px 0', gap: 0 }}>
            {/* ── Import Queue (Gmail) — collapsible, shown when userId + listId available ── */}
            {userId && listId && (
                <Box style={{ marginBottom: 8 }}>
                    <Box
                        onClick={() => setImportQueueOpen(o => !o)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 10px',
                            backgroundColor: '#faf5ff',
                            border: '1px solid #e9d5ff',
                            borderRadius: importQueueOpen ? '8px 8px 0 0' : 8,
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <Typography style={{ fontSize: '0.75rem', fontWeight: 700, color: '#7c3aed', flex: 1 }}>
                            Import Queue
                        </Typography>
                        {importDraftCount > 0 && (
                            <Box
                                style={{
                                    backgroundColor: '#7c3aed',
                                    color: 'white',
                                    borderRadius: 10,
                                    padding: '1px 7px',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                }}
                            >
                                {importDraftCount}
                            </Box>
                        )}
                        {importQueueOpen
                            ? <ExpandLess style={{ fontSize: 18, color: '#7c3aed' }} />
                            : <ExpandMore style={{ fontSize: 18, color: '#7c3aed' }} />
                        }
                    </Box>
                    <Collapse in={importQueueOpen}>
                        <Box style={{ border: '1px solid #e9d5ff', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                            <ImportQueue
                                userId={userId}
                                listId={listId}
                                existingCategories={allCategories}
                                existingTags={allTags}
                                entries={entries}
                                onEntriesChanged={() => {
                                    if (onEntriesChanged) onEntriesChanged();
                                    // Refresh draft count after entries change
                                    getGmailStatus(userId)
                                        .then(s => setImportDraftCount(s.draftCount))
                                        .catch(() => { /* non-critical */ });
                                }}
                            />
                        </Box>
                    </Collapse>
                </Box>
            )}
            {/* Filter bar */}
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', paddingBottom: 8 }}>
                <TextField
                    placeholder="Search description or tag…"
                    value={entrySearch}
                    onChange={e => setEntrySearchP(e.target.value)}
                    size="small"
                    variant="outlined"
                    style={{ width: 160 }}
                    inputProps={{ style: { fontSize: '0.8rem', padding: '6px 10px' } }}
                />
                {(['all', 'DEBIT', 'CREDIT'] as const).map(t => (
                    <Chip
                        key={t}
                        label={t === 'all' ? 'All types' : t === 'DEBIT' ? 'Bills' : 'Income'}
                        size="small"
                        onClick={() => setEntryTypeFilterP(t)}
                        color={entryTypeFilter === t ? 'primary' : 'default'}
                        style={{ fontSize: '0.72rem' }}
                    />
                ))}
                <Chip
                    label="On credit"
                    size="small"
                    onClick={() => setOnCreditFilterP(!onCreditFilter)}
                    style={{
                        fontSize: '0.72rem',
                        backgroundColor: onCreditFilter ? '#7c3aed' : undefined,
                        color: onCreditFilter ? 'white' : undefined,
                        borderColor: onCreditFilter ? '#7c3aed' : '#c4b5fd',
                        border: onCreditFilter ? undefined : '1px solid #c4b5fd',
                    }}
                    variant={onCreditFilter ? 'default' : 'outlined'}
                />
                {allCategories.length > 0 && (
                    <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                        <Select
                            value={entryCategoryFilter}
                            onChange={e => setEntryCategoryFilterP(e.target.value as string)}
                            displayEmpty
                            style={{ fontSize: '0.78rem', height: 32 }}
                        >
                            <MenuItem value="all" style={{ fontSize: '0.78rem' }}>All categories</MenuItem>
                            {allCategories.map((t: string) => (
                                <MenuItem key={t} value={t} style={{ fontSize: '0.78rem' }}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {allTags.length > 0 && (
                    <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
                        <Select
                            value={entryTagFilter}
                            onChange={e => setEntryTagFilterP(e.target.value as string)}
                            displayEmpty
                            style={{ fontSize: '0.78rem', height: 32 }}
                        >
                            <MenuItem value="all" style={{ fontSize: '0.78rem' }}>All tags</MenuItem>
                            {allTags.map((t: string) => (
                                <MenuItem key={t} value={t} style={{ fontSize: '0.78rem' }}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <TextField
                    type="date" label="From" value={entryDateFrom}
                    onChange={e => setEntryDateFromP(e.target.value)}
                    size="small" variant="outlined" InputLabelProps={{ shrink: true }}
                    style={{ width: 148 }} inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
                />
                <TextField
                    type="date" label="To" value={entryDateTo}
                    onChange={e => setEntryDateToP(e.target.value)}
                    size="small" variant="outlined" InputLabelProps={{ shrink: true }}
                    style={{ width: 148 }} inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
                />
                {(entrySearch || entryTypeFilter !== 'all' || entryTagFilter !== 'all' || entryCategoryFilter !== 'all' || onCreditFilter || entryDateFrom || entryDateTo) && (
                    <Button size="small" style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'none', padding: '2px 6px' }}
                        onClick={() => { setEntrySearchP(''); setEntryTypeFilterP('all'); setEntryTagFilterP('all'); setEntryCategoryFilterP('all'); setOnCreditFilterP(false); setEntryDateFromP(''); setEntryDateToP(''); ['ef_search','ef_type','ef_tag','ef_cat','ef_oncredit','ef_from','ef_to'].forEach(k => sessionStorage.removeItem(k)); }}>
                        Clear
                    </Button>
                )}
                {isMobile && !selectMode && (
                    <Button size="small" style={{ fontSize: '0.72rem', color: '#2563eb', textTransform: 'none', padding: '2px 8px', marginLeft: 'auto' }}
                        onClick={() => setSelectMode(true)}>
                        Select
                    </Button>
                )}
                {isMobile && selectMode && (
                    <Button size="small" style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'none', padding: '2px 8px', marginLeft: 'auto' }}
                        onClick={exitSelectMode}>
                        Done
                    </Button>
                )}
            </Box>

            {/* Bulk action bar */}
            {nSelected > 0 && (() => {
                const selectedDebits = filteredEntries.filter((e: any) => selectedIds.has(e.id) && e.type === 'DEBIT');
                const allOnCredit = selectedDebits.length > 0 && selectedDebits.every((e: any) => e.onCredit);
                return (
                <Box style={{ zIndex: 10, display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 8, backgroundColor: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe', flexWrap: 'wrap' }}>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1d4ed8', marginRight: 4 }}>
                        {nSelected} selected
                    </Typography>
                    <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', borderColor: '#f97316', color: '#f97316', padding: '2px 8px' }}
                        onClick={() => handleBulkSetType('DEBIT')}>Set bill</Button>
                    <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', borderColor: '#16a34a', color: '#16a34a', padding: '2px 8px' }}
                        onClick={() => handleBulkSetType('CREDIT')}>Set income</Button>
                    <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', padding: '2px 8px' }}
                        onClick={() => { setBulkCategoryInput(''); setShowBulkCategoryDialog(true); }}>Set category</Button>
                    <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', padding: '2px 8px' }}
                        onClick={() => { setBulkTagInput(''); setShowBulkTagDialog(true); }}>Set tag</Button>
                    <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', padding: '2px 8px' }}
                        onClick={() => { setBulkDateInput(''); setShowBulkDateDialog(true); }}>Set date</Button>
                    <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', padding: '2px 8px' }}
                        onClick={() => { setBulkAmountInput(''); setShowBulkAmountDialog(true); }}>Set amount</Button>
                    {selectedDebits.length > 0 && (
                        <Button size="small" variant={allOnCredit ? 'contained' : 'outlined'} style={{ fontSize: '0.72rem', textTransform: 'none', borderColor: '#7c3aed', color: allOnCredit ? 'white' : '#7c3aed', backgroundColor: allOnCredit ? '#7c3aed' : undefined, padding: '2px 8px' }}
                            onClick={handleBulkToggleOnCredit}>
                            {allOnCredit ? 'Clear credit' : 'On credit'}
                        </Button>
                    )}
                    {userId && listId && (
                        <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', borderColor: '#0891b2', color: '#0891b2', padding: '2px 8px' }}
                            onClick={() => setShowDuplicate(true)}>Duplicate</Button>
                    )}
                    {userId && (
                        <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', borderColor: '#059669', color: '#059669', padding: '2px 8px' }}
                            onClick={() => setShowSaveTemplate(true)}>Save template</Button>
                    )}
                    <Button size="small" variant="outlined" style={{ fontSize: '0.72rem', textTransform: 'none', borderColor: '#dc2626', color: '#dc2626', padding: '2px 8px', marginLeft: 'auto' }}
                        onClick={handleBulkDelete}>Delete</Button>
                </Box>
                );
            })()}

            {entries.length === 0 ? (
                <Box className={classes.emptyState}>No entries yet. Use the + button to add one.</Box>
            ) : filteredEntries.length === 0 ? (
                <Box className={classes.emptyState}>No entries match the current filter.</Box>
            ) : (
                <Box style={VWRAP}>
                    {/* Mobile select mode toolbar */}
                    {isMobile && selectMode && (
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', backgroundColor: '#eff6ff', borderBottom: '1px solid #bfdbfe', flexShrink: 0 }}>
                            <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1d4ed8', flexGrow: 1 }}>
                                {nSelected} selected
                            </Typography>
                            <Button size="small" style={{ fontSize: '0.72rem', textTransform: 'none', color: '#2563eb', padding: '2px 8px' }}
                                onClick={toggleSelectAll}>
                                {allFilteredSelected ? 'Deselect All' : 'Select All'}
                            </Button>
                            <Button size="small" style={{ fontSize: '0.72rem', textTransform: 'none', color: '#64748b', padding: '2px 8px' }}
                                onClick={exitSelectMode}>
                                Done
                            </Button>
                        </Box>
                    )}
                    {/* Header */}
                    <Box style={{ ...VHEAD_ROW, gridTemplateColumns: isMobile ? (selectMode ? ENTRIES_COLS_MOBILE : ENTRIES_COLS_MOBILE_BROWSE) : ENTRIES_COLS }}>
                        {(!isMobile || selectMode) && (
                        <Box style={{ ...VHEAD, padding: '4px 6px' }}>
                            {isMobile ? (
                                <div style={SELECT_CIRCLE} />
                            ) : (
                            <Checkbox size="small" disableRipple className={classes.completeCheck}
                                checked={allFilteredSelected} indeterminate={!allFilteredSelected && nSelected > 0}
                                onChange={toggleSelectAll} />
                            )}
                        </Box>
                        )}
                        <Box style={VHEAD}>
                            <Box style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }} onClick={() => handleSortCol('title')}>
                                Title {entrySortCol === 'title' ? (entrySortDir === 'asc' ? '↑' : '↓') : ''}
                            </Box>
                        </Box>
                        {!isMobile && (
                            <Box style={VHEAD}>
                                <Box style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }} onClick={() => handleSortCol('type')}>
                                    Type {entrySortCol === 'type' ? (entrySortDir === 'asc' ? '↑' : '↓') : ''}
                                </Box>
                            </Box>
                        )}
                        {!isMobile && (
                            <Box style={VHEAD}>
                                <Box style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }} onClick={() => handleSortCol('category')}>
                                    Category {entrySortCol === 'category' ? (entrySortDir === 'asc' ? '↑' : '↓') : ''}
                                </Box>
                            </Box>
                        )}
                        {!isMobile && (
                            <Box style={VHEAD}>
                                <Box style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }} onClick={() => handleSortCol('tag')}>
                                    Tag {entrySortCol === 'tag' ? (entrySortDir === 'asc' ? '↑' : '↓') : ''}
                                </Box>
                            </Box>
                        )}
                        <Box style={{ ...VHEAD, justifyContent: 'flex-end' }}>
                            <Box style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }} onClick={() => handleSortCol('amount')}>
                                Amount {entrySortCol === 'amount' ? (entrySortDir === 'asc' ? '↑' : '↓') : ''}
                            </Box>
                        </Box>
                        <Box style={VHEAD}>
                            <Box style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }} onClick={() => handleSortCol('date')}>
                                Date {entrySortCol === 'date' ? (entrySortDir === 'asc' ? '↑' : '↓') : ''}
                            </Box>
                        </Box>
                        {!isMobile && <Box style={VHEAD} />}
                    </Box>
                    {/* Virtual rows */}
                    <AutoHeight>
                        {(height: number) => (
                            <FixedSizeList height={height} width="100%" itemCount={filteredEntries.length} itemSize={ROW_HEIGHT} overscanCount={8} itemData={entriesData}>
                                {EntriesRow}
                            </FixedSizeList>
                        )}
                    </AutoHeight>
                    {/* Total row */}
                    {isMobile ? (
                        <Box style={{ ...VTOTAL, gridTemplateColumns: selectMode ? ENTRIES_COLS_MOBILE : ENTRIES_COLS_MOBILE_BROWSE }}>
                            {selectMode && <Box />}
                            <Box style={{ padding: '0 10px', fontWeight: 700 }}>{selectedEntriesTotals ? `${nSelected} selected` : 'Total'}</Box>
                            <Box style={{ padding: '0 10px', textAlign: 'right', color: totalFilteredNet >= 0 ? '#16a34a' : '#dc2626' }}>
                                {fmt(totalFilteredNet)}
                            </Box>
                            <Box />
                        </Box>
                    ) : (
                        <Box style={{ ...VTOTAL, gridTemplateColumns: ENTRIES_COLS }}>
                            <Box />
                            <Box style={{ padding: '0 10px' }}>{selectedEntriesTotals ? `${nSelected} selected` : 'Total'}</Box>
                            <Box />
                            <Box />
                            <Box />
                            <Box style={{ padding: '0 10px', textAlign: 'right' }}>
                                <span style={{ color: '#16a34a', marginRight: 10 }}>+{fmt(totalFilteredCredits)}</span>
                                <span style={{ color: '#dc2626', marginRight: 10 }}>{fmt(-totalFilteredDebits)}</span>
                                <span style={{ color: totalFilteredNet >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>= {fmt(totalFilteredNet)}</span>
                            </Box>
                            <Box />
                            <Box />
                        </Box>
                    )}
                </Box>
            )}

            {/* Add buttons */}
            <Box style={{ padding: '8px 0 12px', display: 'flex', gap: '8px' }}>
                <Button className={classes.addRowBtn} startIcon={<Add />} onClick={() => setAddFormType('DEBIT')}>Add bill</Button>
                <Button className={classes.addRowBtn} startIcon={<Add />} onClick={() => setAddFormType('CREDIT')}>Add income</Button>
                <Button className={classes.addRowBtn} onClick={() => setShowBulkAdd(true)} style={{ color: '#64748b' }}>Bulk Add</Button>
            </Box>

            {/* Add entry dialog */}
            <EntryFormDialog
                open={addFormType !== null}
                dialogTitle={addFormType === 'CREDIT' ? 'Add Income' : 'Add Bill'}
                saveLabel={addFormType === 'CREDIT' ? 'Add Income' : 'Add Bill'}
                initialValues={{ type: addFormType ?? 'DEBIT' }}
                existingTags={allTags}
                existingCategories={allCategories}
                onSave={handleInlineSave}
                onClose={() => setAddFormType(null)}
            />

            <BulkAddDialog
                open={showBulkAdd}
                existingCategories={allCategories}
                existingTags={allTags}
                onSave={handleBulkAddSave}
                onClose={() => setShowBulkAdd(false)}
            />

            {/* Duplicate dialog */}
            <DuplicateDialog
                open={showDuplicate}
                entryCount={nSelected}
                onConfirm={handleDuplicate}
                onClose={() => setShowDuplicate(false)}
            />

            {/* Save as template dialog */}
            {userId && (
                <SaveTemplateDialog
                    open={showSaveTemplate}
                    entries={filteredEntries.filter((e: any) => selectedIds.has(e.id))}
                    userId={userId}
                    onSaved={() => { setShowSaveTemplate(false); setSelectedIds(new Set()); }}
                    onClose={() => setShowSaveTemplate(false)}
                />
            )}

            {/* Bulk set date dialog */}
            <Dialog open={showBulkDateDialog} onClose={() => setShowBulkDateDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Set date for {nSelected} {nSelected === 1 ? 'entry' : 'entries'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Date"
                        type="date"
                        value={bulkDateInput}
                        onChange={e => setBulkDateInput(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        style={{ marginTop: 8 }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBulkDateDialog(false)} style={{ color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleBulkSetDate} disabled={!bulkDateInput}>Apply</Button>
                </DialogActions>
            </Dialog>

            {/* Bulk set amount dialog */}
            <Dialog open={showBulkAmountDialog} onClose={() => setShowBulkAmountDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Set amount for {nSelected} {nSelected === 1 ? 'entry' : 'entries'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Amount ($)"
                        type="number"
                        value={bulkAmountInput}
                        onChange={e => setBulkAmountInput(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{ inputProps: { step: '0.01', min: '0' } }}
                        style={{ marginTop: 8 }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBulkAmountDialog(false)} style={{ color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleBulkSetAmount} disabled={!bulkAmountInput || isNaN(parseFloat(bulkAmountInput))}>Apply</Button>
                </DialogActions>
            </Dialog>

            {/* Bulk set category dialog */}
            <Dialog open={showBulkCategoryDialog} onClose={() => setShowBulkCategoryDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Set category for {nSelected} {nSelected === 1 ? 'entry' : 'entries'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Category"
                        value={bulkCategoryInput}
                        onChange={e => setBulkCategoryInput(e.target.value)}
                        fullWidth size="small" variant="outlined" autoFocus
                        placeholder="e.g. Housing" style={{ marginTop: 8 }}
                    />
                    {allCategories.length > 0 && (
                        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                            {allCategories.map((t: string) => (
                                <Chip key={t} label={t} size="small" onClick={() => setBulkCategoryInput(t)}
                                    color={bulkCategoryInput === t ? 'primary' : 'default'}
                                    style={{ fontSize: '0.72rem', cursor: 'pointer' }} />
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBulkCategoryDialog(false)} style={{ color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleBulkSetCategory}>Apply</Button>
                </DialogActions>
            </Dialog>

            {/* Bulk set tag dialog */}
            <Dialog open={showBulkTagDialog} onClose={() => setShowBulkTagDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Set tag for {nSelected} {nSelected === 1 ? 'entry' : 'entries'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Tags (comma separated)"
                        value={bulkTagInput}
                        onChange={e => setBulkTagInput(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        autoFocus
                        placeholder="e.g. work, travel"
                        style={{ marginTop: 8 }}
                    />
                    {allTags.length > 0 && (
                        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                            {allTags.map((t: string) => (
                                <Chip key={t} label={t} size="small"
                                    onClick={() => setBulkTagInput(prev => {
                                        const existing = prev ? prev.split(',').map(s => s.trim()).filter(Boolean) : [];
                                        return existing.includes(t) ? prev : [...existing, t].join(', ');
                                    })}
                                    color={bulkTagInput.split(',').map(s => s.trim()).includes(t) ? 'primary' : 'default'}
                                    style={{ fontSize: '0.72rem', cursor: 'pointer' }} />
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBulkTagDialog(false)} style={{ color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleBulkSetTag}>Apply</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Export setters for parent to call when navigating from ProjectionTab
export type { EntriesTabProps };
